import { sharedDocumentFieldsSchema } from "./document";
import { themeType } from "./global";
import { addDays } from "date-fns";
import { z } from "zod";

export const estimateStatusEnum = z.enum(["draft", "not-sent", "sent", "approved", "rejected", "expired", "cancelled"]);
export const estimateValueTypesEnum = z.enum(["fixed", "percentage"]);

export const baseEstimateSchema = sharedDocumentFieldsSchema.merge(themeType).extend({
  estimateNumber: z.string().min(1, { message: "Estimate number is required" }),
  status: estimateStatusEnum.default("draft").optional(),
  validUntil: z.date().nullable(),
  projectId: z.string().optional().nullable(),
});

export const createEstimateSchema = baseEstimateSchema;

export const editEstimateSchema = baseEstimateSchema.extend({
  id: z.string(),
});

export const getEstimateSchema = z.object({
  id: z.string(),
});

export const EditEstimatePageSchema = z.object({ id: z.string() });

export const updateEstimateStatusSchema = z.object({
  status: estimateStatusEnum,
});

export const getEstimatesParamsSchema = z.object({
  // Entity filters
  relationId: z.string().optional(),
  projectId: z.string().optional(),
  // Text search filters
  estimateNumber: z.string().optional(),
  client: z.string().optional(),
  // Status filter
  status: z
    .union([estimateStatusEnum, z.array(estimateStatusEnum)])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  // Date range filters
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
  validUntilFrom: z.coerce.date().optional(),
  validUntilTo: z.coerce.date().optional(),
  createdAtFrom: z.coerce.date().optional(),
  createdAtTo: z.coerce.date().optional(),
});

export type ZodCreateEstimateSchema = z.infer<typeof createEstimateSchema>;
export type ZodEditEstimateSchema = z.infer<typeof editEstimateSchema>;
export type ZodGetEstimatesParams = z.infer<typeof getEstimatesParamsSchema>;

export const createEstimateSchemaDefaultValues: ZodCreateEstimateSchema = {
  status: "draft",

  organizationName: "",
  organizationLogo: "",
  organizationAddressLine1: "",
  organizationAddressLine2: "",
  organizationCity: "",
  organizationState: "",
  organizationZipcode: "",
  organizationCountry: "",

  relationId: "",
  relationName: "",
  relationAddressLine1: "",
  relationAddressLine2: "",
  relationCity: "",
  relationState: "",
  relationZipcode: "",
  relationCountry: "",

  model: "default",
  language: "en",
  primaryColor: "#635CFF",
  textBeforeItems: "",
  textAfterItems: "",
  footer: "",

  currency: "USD",
  exchangeRates: null,
  estimateNumber: "",
  date: new Date(),
  validUntil: addDays(new Date(), 30),
  showItemsWithTax: false,
  total: 0,
  subtotal: 0,

  items: [],
  taxes: [],
};

export type EstimateStatus = z.infer<typeof estimateStatusEnum>;
export type EstimateValueType = z.infer<typeof estimateValueTypesEnum>;

export const estimateSchema = baseEstimateSchema.extend({
  id: z.string(),
  sentDate: z.date().optional().nullable(),
  openDate: z.date().optional().nullable(),
  cancelDate: z.date().optional().nullable(),
  organizationId: z.string(),
  userId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type EstimateSchema = z.infer<typeof estimateSchema>;
